import { useEffect, useMemo, useRef, useState } from 'react';
import HostTopNav from './HostTopNav';
import ReviewEditButton from './ReviewEditButton';
import { LuCheck, LuClock, LuChevronDown } from "react-icons/lu";

/* ======================
   DURATIONS (minutes)
====================== */
const OPENING_DURATION = 5;
const ICEBREAKER_DURATION = 10;
const TRANSITION_DURATION = 5;
const CLOSING_DURATION = 5;

/* ======================
   TIME HELPERS
====================== */
function addMinutes(time, mins) {
  if (!time) return '';
  const [h, m] = time.split(':').map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return '';

  const d = new Date();
  d.setHours(h, m + mins, 0, 0);
  return d.toTimeString().slice(0, 5);
}

function formatTime12h(time) {
  if (!time) return '';
  const [h, m] = time.split(':').map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return '';

  const d = new Date();
  d.setHours(h, m, 0, 0);
  return d.toLocaleTimeString([], {
    hour: 'numeric',
    minute: '2-digit',
  });
}

function timeToMinutes(time) {
  if (!time) return null;
  const [h, m] = time.split(':').map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return h * 60 + m;
}

// Treat times earlier than eventStartTime as "next day" for comparisons.
function toTimelineDate(time, eventStartTime) {
  if (!time) return null;

  const [h, m] = time.split(':').map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;

  const d = new Date();
  d.setSeconds(0, 0);
  d.setHours(h, m, 0, 0);

  const t = timeToMinutes(time);
  const anchor = timeToMinutes(eventStartTime);

  if (t != null && anchor != null && t < anchor) {
    d.setDate(d.getDate() + 1);
  }

  return d;
}

function diffMinutes(fromTime, toTime, eventStartTime) {
  const a = toTimelineDate(fromTime, eventStartTime);
  const b = toTimelineDate(toTime, eventStartTime);
  if (!a || !b) return null;
  return Math.round((b - a) / 60000);
}

function gapMinutes(prevEnd, nextStart, eventStartTime) {
  const d = diffMinutes(prevEnd, nextStart, eventStartTime);
  if (d == null) return 0;
  return d > 0 ? d : 0;
}

function minutesLabel(n) {
  return n === 1 ? 'minute' : 'minutes';
}

/* ======================
   CUSTOM TIME DROPDOWN
   (matches HostBasicInfo UI/behavior)
====================== */
function TimeSelectDropdown({ ui, value, onChange, reviewLocked, lockedFieldStyle }) {
  const isDesktop =
  typeof window !== 'undefined' &&
  window.matchMedia('(pointer: fine)').matches;

  const ref = useRef(null);
  const [open, setOpen] = useState(false);

  const [hh12, setHh12] = useState('');
  const [mm, setMm] = useState('');
  const [ampm, setAmpm] = useState('AM');

  // close on outside click
  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  // if locked, force close
  useEffect(() => {
    if (reviewLocked) setOpen(false);
  }, [reviewLocked]);

  // parse incoming value ("HH:MM" 24h)
  useEffect(() => {
    if (!value || !/^\d{2}:\d{2}$/.test(value)) {
      setHh12('');
      setMm('');
      setAmpm('AM');
      return;
    }

    const [H, M] = value.split(':').map(Number);
    if (Number.isNaN(H) || Number.isNaN(M)) return;

    const nextAmpm = H >= 12 ? 'PM' : 'AM';
    let h = H % 12;
    if (h === 0) h = 12;

    setHh12(String(h).padStart(2, '0')); // 01..12
    setMm(String(M).padStart(2, '0'));   // 00..59
    setAmpm(nextAmpm);
  }, [value]);

  function commit(nextH12 = hh12, nextM = mm, nextA = ampm) {
    if (!nextH12 || nextM === '') return;

    const h12 = Number(nextH12);
    const m = Number(nextM);
    if (!Number.isFinite(h12) || h12 < 1 || h12 > 12) return;
    if (!Number.isFinite(m) || m < 0 || m > 59) return;

    let h24 = h12 % 12;
    if (nextA === 'PM') h24 += 12;

    onChange(`${String(h24).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
  }

  const hourOptions = Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, '0'));
  const minuteOptions = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, '0'));

  const displayValue = hh12 && mm ? `${hh12}:${mm} ${ampm}` : '';

  if (isDesktop) {
  return (
    <input
      type="time"
      disabled={reviewLocked}
      value={value || ''}
      onChange={(e) => {
        if (reviewLocked) return;
        onChange(e.target.value);
      }}
      style={{
        ...ui.input,
        ...lockedFieldStyle,
        cursor: reviewLocked ? 'default' : 'pointer',
      }}
    />
  );
}


  
  return (
    
    <div ref={ref} style={{ position: 'relative' }}>
      {/* Trigger */}
      <button
        type="button"
        disabled={reviewLocked}
        onClick={() => {
          if (reviewLocked) return;
          setOpen((v) => !v);
        }}
        style={{
          ...ui.input,
          ...lockedFieldStyle,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 10,
          backgroundColor: reviewLocked ? '#eef0f2' : '#fff',
          cursor: reviewLocked ? 'default' : 'pointer',
          textAlign: 'left',
          paddingRight: 12,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
          <LuClock size={18} style={{ color: '#9ca3af', flexShrink: 0 }} />
   <span
  style={{
    fontFamily: 'inherit',
    fontSize: 12,
    fontVariantNumeric: 'tabular-nums',
    fontWeight: 500,
    letterSpacing: 0.5,
    color: displayValue ? '#0b0b0b' : '#9ca3af',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  }}
>
  {displayValue || 'Select time'}
</span>

        </div>

<LuChevronDown
  aria-hidden
  style={{ fontSize: 16, color: '#9ca3af', lineHeight: 0, flexShrink: 0 }}
/>
      </button>

      {/* Dropdown panel */}
      {open && !reviewLocked && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            left: 0,
            right: 0,
            zIndex: 80,
            background: '#fff',
            borderRadius: 12,
            boxShadow: '0 10px 26px rgba(0,0,0,0.14)',
            border: '1px solid #eef0f2',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 1fr', // HH | MM | AM/PM
              gap: 0,
              padding: 10,
            }}
          >
            {(() => {
              const colStyle = {
                maxHeight: 220,
                overflowY: 'auto',
                padding: '6px 0',
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
              };

              const itemStyle = (active) => ({
                width: '78%',
                maxWidth: 64,
                boxSizing: 'border-box',
                border: 'none',
                background: active ? '#f3f4f6' : 'transparent',
                borderRadius: 10,
                padding: '8px 0',
                textAlign: 'center',
                fontFamily: 'inherit',
                fontVariantNumeric: 'tabular-nums',
                fontWeight: active ? 900 : 600,
letterSpacing: 0.5,
fontSize: 12,

                color: '#0b0b0b',
                cursor: 'pointer',
                margin: '0 auto',
              });

              return (
                <>
                  {/* HH */}
                  <div style={colStyle}>
                    {hourOptions.map((h) => (
                      <button
                        key={h}
                        type="button"
                        style={itemStyle(h === hh12)}
                        onClick={() => {
                          setHh12(h);
                          commit(h, mm, ampm);
                        }}
                      >
                        {h}
                      </button>
                    ))}
                  </div>

                  {/* MM */}
                  <div style={colStyle}>
                    {minuteOptions.map((m) => (
                      <button
                        key={m}
                        type="button"
                        style={itemStyle(m === mm)}
                        onClick={() => {
                          setMm(m);
                          commit(hh12, m, ampm);
                          setOpen(false);
                        }}
                      >
                        {m}
                      </button>
                    ))}
                  </div>

                  {/* AM/PM */}
                  <div style={colStyle}>
                    {['AM', 'PM'].map((a) => (
                      <button
                        key={a}
                        type="button"
                        style={itemStyle(a === ampm)}
                        onClick={() => {
                          setAmpm(a);
                          commit(hh12, mm, a);
                        }}
                      >
                        {a}
                      </button>
                    ))}
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
}

export default function HostAgenda({
  ui,
  reviewLocked,
  setAgendaComplete,
  inModal = false,
  isReviewFlow,
  onEdit,
  onCancel,
  submissionStatus,
  step,
  totalSteps,

  /* 🔑 from Basic Info */
  eventStartTime,
  eventEndTime,

  /* 🔑 lifted agenda times */
  welcomeStart,
  setWelcomeStart,
  welcomeEnd,
  setWelcomeEnd,

  openingStart,
  setOpeningStart,
  openingEnd,
  setOpeningEnd,

  icebreakerStart,
  setIcebreakerStart,
  icebreakerEnd,
  setIcebreakerEnd,

  transitionStart,
  setTransitionStart,
  transitionEnd,
  setTransitionEnd,

  closingStart,
  setClosingStart,
  closingEnd,
  setClosingEnd,

  /* agenda content */
  icebreakerHowItWorks,
  setIcebreakerHowItWorks,

  primaryName,
  setPrimaryName,
  primaryStart,
  setPrimaryStart,
  primaryEnd,
  setPrimaryEnd,
  primaryDescription,
  setPrimaryDescription,

  secondaryName,
  setSecondaryName,
  secondaryStart,
  setSecondaryStart,
  secondaryEnd,
  setSecondaryEnd,
  secondaryDescription,
  setSecondaryDescription,

  acknowledgedSafety,
  setAcknowledgedSafety,
}) {
  /* ======================
     COMPUTED / DEFAULTS
     (everything is editable EXCEPT welcome)
  ====================== */

  const lockedFieldStyle = reviewLocked
    ? {
        backgroundColor: '#eef0f2',
        borderColor: '#e5e7eb',
        cursor: 'default',
        opacity: 1,
        color: '#0b0b0b',
      }
    : {};

  const forcedLockedFieldStyle = {
    backgroundColor: '#eef0f2',
    borderColor: '#e5e7eb',
    cursor: 'default',
    opacity: 1,
    color: '#0b0b0b',
  };

  // Welcome Guests (15 minutes before event start) — forced
  useEffect(() => {
    if (!eventStartTime) return;

    setWelcomeEnd(eventStartTime);
    setWelcomeStart(addMinutes(eventStartTime, -15));
  }, [eventStartTime, setWelcomeStart, setWelcomeEnd]);

  // ─────────────────────────
  // Editable times + "touched" flags
  // ─────────────────────────
  const [openingStartTouched, setOpeningStartTouched] = useState(false);
  const [openingEndTouched, setOpeningEndTouched] = useState(false);

  const [icebreakerStartTouched, setIcebreakerStartTouched] = useState(false);
  const [icebreakerEndTouched, setIcebreakerEndTouched] = useState(false);

  const [primaryStartTouched, setPrimaryStartTouched] = useState(false);
  const [primaryEndTouched, setPrimaryEndTouched] = useState(false);

  const [transitionStartTouched, setTransitionStartTouched] = useState(false);
  const [transitionEndTouched, setTransitionEndTouched] = useState(false);

  const [secondaryStartTouched, setSecondaryStartTouched] = useState(false);

  const [secondaryEndTouched, setSecondaryEndTouched] = useState(false);
  const secondaryEndAutoRef = useRef(false);

  const [closingStartTouched, setClosingStartTouched] = useState(false);
  const [closingEndTouched, setClosingEndTouched] = useState(false);

  
  // ─────────────────────────
  // Default chain (only updates fields the user has NOT touched)
  // ─────────────────────────

  // Opening start defaults to event start
  useEffect(() => {
    if (!openingStartTouched) setOpeningStart(eventStartTime || '');
  }, [eventStartTime, openingStartTouched]);

  // Opening end defaults to openingStart + duration
  const openingEndDefault = useMemo(
    () => addMinutes(openingStart, OPENING_DURATION),
    [openingStart]
  );
  useEffect(() => {
    if (!openingEndTouched) setOpeningEnd(openingEndDefault);
  }, [openingEndDefault, openingEndTouched]);

  // Icebreaker start defaults to opening end
  useEffect(() => {
    if (!icebreakerStartTouched) setIcebreakerStart(openingEnd || '');
  }, [openingEnd, icebreakerStartTouched]);

  // Icebreaker end defaults to icebreakerStart + duration
  const icebreakerEndDefault = useMemo(
    () => addMinutes(icebreakerStart, ICEBREAKER_DURATION),
    [icebreakerStart]
  );
  useEffect(() => {
    if (!icebreakerEndTouched) setIcebreakerEnd(icebreakerEndDefault);
  }, [icebreakerEndDefault, icebreakerEndTouched]);

  // Primary start defaults to icebreaker end
  useEffect(() => {
    if (!primaryStartTouched) setPrimaryStart(icebreakerEnd || '');
  }, [icebreakerEnd, primaryStartTouched]);

  // Transition defaults (based on primary end)
  useEffect(() => {
    if (!transitionStartTouched) setTransitionStart(primaryEnd || '');
  }, [primaryEnd, transitionStartTouched]);

  const transitionEndDefault = useMemo(
    () => addMinutes(transitionStart, TRANSITION_DURATION),
    [transitionStart]
  );
  useEffect(() => {
    if (!transitionEndTouched) setTransitionEnd(transitionEndDefault);
  }, [transitionEndDefault, transitionEndTouched]);

  // Secondary defaults AFTER primary end exists:
  // - start: transition end
  // - end: event end - closing duration
  const secondaryEndDefault = useMemo(
    () => addMinutes(eventEndTime, -CLOSING_DURATION),
    [eventEndTime]
  );

  useEffect(() => {
    // If primaryEnd is cleared, clear secondary defaults (only if user hasn't touched them)
    if (!primaryEnd) {
      if (!secondaryStartTouched) setSecondaryStart('');
      if (!secondaryEndTouched && secondaryEndAutoRef.current) {
        setSecondaryEnd('');
        secondaryEndAutoRef.current = false;
      }
      return;
    }

    // Primary end exists → we can suggest secondary
    if (!secondaryStartTouched) {
      setSecondaryStart(transitionEnd || '');
    }

    if (!secondaryEndTouched && eventEndTime) {
      // Only overwrite if empty OR previously auto-filled
      if (!secondaryEnd || secondaryEndAutoRef.current) {
        setSecondaryEnd(secondaryEndDefault);
        secondaryEndAutoRef.current = true;
      }
    }
  }, [
    primaryEnd,
    transitionEnd,
    secondaryStartTouched,
    secondaryEndTouched,
    eventEndTime,
    secondaryEndDefault,
    secondaryEnd,
    setSecondaryEnd,
  ]);

  // Closing defaults (based on secondary end)
  useEffect(() => {
    if (!closingStartTouched) setClosingStart(secondaryEnd || '');
  }, [secondaryEnd, closingStartTouched]);

  const closingEndDefault = useMemo(
    () => addMinutes(closingStart, CLOSING_DURATION),
    [closingStart]
  );
  useEffect(() => {
    if (!closingEndTouched) setClosingEnd(closingEndDefault);
  }, [closingEndDefault, closingEndTouched]);

  // Full agenda end === closing end (editable)
  const agendaEnd = closingEnd;

  // Extended / shortened warnings (compare on timeline)
  const plannedEndDate = useMemo(
    () => toTimelineDate(eventEndTime, eventStartTime),
    [eventEndTime, eventStartTime]
  );
  const agendaEndDate = useMemo(
    () => toTimelineDate(agendaEnd, eventStartTime),
    [agendaEnd, eventStartTime]
  );

  const isExtended =
    plannedEndDate && agendaEndDate && agendaEndDate > plannedEndDate;

  const isShortened =
    plannedEndDate && agendaEndDate && agendaEndDate < plannedEndDate;

  // Gap warnings (as requested)
  const gapIntroToPrimary = useMemo(
    () => gapMinutes(icebreakerEnd, primaryStart, eventStartTime),
    [icebreakerEnd, primaryStart, eventStartTime]
  );
  const gapPrimaryToTransition = useMemo(
    () => gapMinutes(primaryEnd, transitionStart, eventStartTime),
    [primaryEnd, transitionStart, eventStartTime]
  );
  const gapTransitionToSecondary = useMemo(
    () => gapMinutes(transitionEnd, secondaryStart, eventStartTime),
    [transitionEnd, secondaryStart, eventStartTime]
  );

  const requiredAsteriskStyle = {
    color: '#dc2626',
    marginLeft: 4,
    fontWeight: 900,
  };

  function RequiredIndicator({ complete }) {
    if (complete) {
      return (
        <LuCheck
          size={14}
          style={{
            marginLeft: 6,
            color: '#16a34a',
            strokeWidth: 3,
            position: 'relative',
            top: -4,
          }}
        />
      );
    }

    return <span style={requiredAsteriskStyle}>*</span>;
  }

  const welcomeComplete = Boolean(welcomeStart && welcomeEnd);
  const openingComplete = Boolean(openingStart && openingEnd);

  const icebreakerComplete = Boolean(
    icebreakerStart &&
      icebreakerEnd &&
      icebreakerHowItWorks?.trim()
  );

  const primaryComplete = Boolean(
    primaryName?.trim() &&
      primaryStart &&
      primaryEnd &&
      primaryDescription?.trim()
  );

  const transitionComplete = Boolean(transitionStart && transitionEnd);

  const secondaryComplete = Boolean(
    secondaryName?.trim() &&
      secondaryStart &&
      secondaryEnd &&
      secondaryDescription?.trim()
  );

  const closingComplete = Boolean(closingStart && closingEnd);

  const agendaComplete =
    welcomeComplete &&
    openingComplete &&
    icebreakerComplete &&
    primaryComplete &&
    transitionComplete &&
    secondaryComplete &&
    closingComplete;

  useEffect(() => {
    if (typeof setAgendaComplete === 'function') {
      setAgendaComplete(agendaComplete);
    }
  }, [agendaComplete, setAgendaComplete]);

  return (
    <>
      <HostTopNav
        title={isReviewFlow ? 'Update Event' : 'Add Event'}
        step={step}
        totalSteps={totalSteps}
        backTo="/host"
          hideChrome={inModal}

      />

      <div style={{ padding: '0 16px 20px' }}>
        <div style={{ ...ui.block, borderTop: 'none', marginTop: 0 }}>
          <div style={ui.blockHeader}>
            <div style={ui.sectionLabel}>Agenda</div>

            {isReviewFlow && submissionStatus !== 'rejected' && (
              <ReviewEditButton
                isReviewFlow={isReviewFlow}
                reviewLocked={reviewLocked}
                onEdit={onEdit}
                onCancel={onCancel}
              />
            )}
          </div>

          {/* 1. WELCOME GUESTS */}
          <div style={ui.agendaSection}>
            <div style={ui.agendaTitle}>
              1. WELCOME GUESTS
              <RequiredIndicator complete={welcomeComplete} />
            </div>
            <div style={ui.agendaPurpose}>
              <strong>Purpose:</strong> Warm people up as they arrive and make sure no
              one is lost or awkward.
            </div>

            {/* Start/End time row (forced/disabled) */}
            <div style={{ marginTop: 10 }}>
              <div style={ui.grid2}>
                <div>
                  <div style={ui.fieldLabel}>Start time</div>
                  <TimeSelectDropdown
                    ui={ui}
                    value={welcomeStart}
                    reviewLocked={true}
                    lockedFieldStyle={forcedLockedFieldStyle}
                    onChange={() => {}}
                  />
                </div>
                <div>
                  <div style={ui.fieldLabel}>End time</div>
                  <TimeSelectDropdown
                    ui={ui}
                    value={welcomeEnd}
                    reviewLocked={true}
                    lockedFieldStyle={forcedLockedFieldStyle}
                    onChange={() => {}}
                  />
                </div>
              </div>
            </div>

            <div style={ui.agendaBody}>
              Arrive early and stand where people enter.
              <br />
              Greet each guest, say your name, and ask for theirs.
              <br />
              Use the app to drop a message or answer “where are you?” so everyone
              can find the spot.
              <br />
              Be friendly and relaxed; start small talk and lightly introduce people
              to each other.
            </div>
          </div>

          <div style={ui.divider} />

          {/* 2. OPENING INTRODUCTION */}
          <div style={ui.agendaSection}>
            <div style={ui.agendaTitle}>
              2. OPENING INTRODUCTION
              <RequiredIndicator complete={openingComplete} />
            </div>
            <div style={ui.agendaPurpose}>
              <strong>Purpose:</strong> Officially start the event, set context, and
              explain the plan.
            </div>

            <div style={ui.agendaMeta}>Start time: Event start time</div>
            <div style={ui.agendaMeta}>(Takes about 2–5 minutes total.)</div>

            {/* Start/End time row (editable) */}
            <div style={{ marginTop: 10 }}>
              <div style={ui.grid2}>
                <div>
                  <div style={ui.fieldLabel}>Start time</div>
                  <TimeSelectDropdown
                    ui={ui}
                    value={openingStart}
                    reviewLocked={reviewLocked}
                    lockedFieldStyle={lockedFieldStyle}
                    onChange={(v) => {
                      if (reviewLocked) return;
                      setOpeningStartTouched(true);
                      setOpeningStart(v);
                    }}
                  />
                </div>

                <div>
                  <div style={ui.fieldLabel}>End time</div>
                  <TimeSelectDropdown
                    ui={ui}
                    value={openingEnd}
                    reviewLocked={reviewLocked}
                    lockedFieldStyle={lockedFieldStyle}
                    onChange={(v) => {
                      if (reviewLocked) return;
                      setOpeningEndTouched(true);
                      setOpeningEnd(v);
                    }}
                  />
                </div>
              </div>
            </div>

            <div style={ui.agendaBody}>
              <div style={{ fontWeight: 800, marginTop: 8 }}>
                What you’ll cover:
              </div>
              Brief welcome and who you are.
              <br />
              Simple outline of the night (icebreaker → primary → secondary →
              closing).
              <br />
              Basic vibe / expectations (kind, inclusive, no creepy behavior).
              <br />
              Let people know you’ll start with a quick icebreaker so they can meet
              each other.
            </div>
          </div>

          <div style={ui.divider} />

          {/* 3. ICEBREAKER ACTIVITY */}
          <div style={ui.agendaSection}>
            <div style={ui.agendaTitle}>
              3. ICEBREAKER ACTIVITY
              <RequiredIndicator complete={icebreakerComplete} />
            </div>
            <div style={ui.agendaPurpose}>
              <strong>Purpose:</strong> Get people talking to each other quickly, not
              just to you.
            </div>
            <div style={ui.agendaMeta}>
              Start time: Right after Opening Introduction
            </div>
            <div style={ui.agendaMeta}>(Takes about 5–10 minutes total.)</div>

            {/* Start/End time row (editable) */}
            <div style={{ marginTop: 10 }}>
              <div style={ui.grid2}>
                <div>
                  <div style={ui.fieldLabel}>Start time</div>
                  <TimeSelectDropdown
                    ui={ui}
                    value={icebreakerStart}
                    reviewLocked={reviewLocked}
                    lockedFieldStyle={lockedFieldStyle}
                    onChange={(v) => {
                      if (reviewLocked) return;
                      setIcebreakerStartTouched(true);
                      setIcebreakerStart(v);
                    }}
                  />
                </div>

                <div>
                  <div style={ui.fieldLabel}>End time</div>
                  <TimeSelectDropdown
                    ui={ui}
                    value={icebreakerEnd}
                    reviewLocked={reviewLocked}
                    lockedFieldStyle={lockedFieldStyle}
                    onChange={(v) => {
                      if (reviewLocked) return;
                      setIcebreakerEndTouched(true);
                      setIcebreakerEnd(v);
                    }}
                  />
                </div>
              </div>
            </div>

            <div style={{ marginTop: 10 }}>
              <div style={ui.fieldLabel}>How it works (1–2 sentences):</div>
              <textarea
                disabled={reviewLocked}
                style={{
                  ...ui.textarea,
                  ...(reviewLocked
                    ? { backgroundColor: '#eef0f2', borderColor: '#e5e7eb' }
                    : {}),
                }}
                value={icebreakerHowItWorks}
                onChange={(e) => {
                  if (reviewLocked) return;
                  setIcebreakerHowItWorks(e.target.value);
                }}
                placeholder="Describe the icebreaker in 1–2 sentences"
              />
            </div>
          </div>

          <div style={ui.divider} />

          {/* 4. PRIMARY ACTIVITY */}
          <div style={ui.agendaSection}>
            <div style={ui.agendaTitle}>
              4. PRIMARY ACTIVITY
              <RequiredIndicator complete={primaryComplete} />
            </div>
            <div style={ui.agendaPurpose}>
              <strong>Purpose:</strong> The main thing people are coming to do.
            </div>

            {/* Gap warning: Icebreaker → Primary */}
            {gapIntroToPrimary > 0 && (
              <div style={{ marginTop: 6, fontSize: 12, color: '#6b7280' }}>
                ℹ️ There is a {gapIntroToPrimary} {minutesLabel(gapIntroToPrimary)} gap
                between the Icebreaker and Primary. Consider adjusting start times.
              </div>
            )}

            <div style={{ marginTop: 10 }}>
              <div style={ui.fieldLabel}>Primary activity name:</div>
              <input
                disabled={reviewLocked}
                style={{
                  ...ui.input,
                  ...(reviewLocked
                    ? { backgroundColor: '#eef0f2', borderColor: '#e5e7eb' }
                    : {}),
                }}
                value={primaryName}
                onChange={(e) => {
                  if (reviewLocked) return;
                  setPrimaryName(e.target.value);
                }}
                placeholder="Enter primary activity name"
              />
            </div>

            <div style={ui.grid2}>
              <div>
                <div style={ui.fieldLabel}>Start time:</div>
                <TimeSelectDropdown
                  ui={ui}
                  value={primaryStart}
                  reviewLocked={reviewLocked}
                  lockedFieldStyle={lockedFieldStyle}
                  onChange={(v) => {
                    if (reviewLocked) return;
                    setPrimaryStartTouched(true);
                    setPrimaryStart(v);
                  }}
                />
              </div>

              <div>
                <div style={ui.fieldLabel}>End time:</div>
                <TimeSelectDropdown
                  ui={ui}
                  value={primaryEnd}
                  reviewLocked={reviewLocked}
                  lockedFieldStyle={lockedFieldStyle}
                  onChange={(v) => {
                    if (reviewLocked) return;
                    setPrimaryEndTouched(true);
                    setPrimaryEnd(v);
                  }}
                />
              </div>
            </div>

            <div style={{ marginTop: 6 }}>
              <div style={ui.fieldLabel}>Short description of what happens:</div>
              <textarea
                disabled={reviewLocked}
                style={{
                  ...ui.textareaLarge,
                  ...(reviewLocked
                    ? { backgroundColor: '#eef0f2', borderColor: '#e5e7eb' }
                    : {}),
                }}
                value={primaryDescription}
                onChange={(e) => {
                  if (reviewLocked) return;
                  setPrimaryDescription(e.target.value);
                }}
                placeholder="Describe what happens during the primary activity"
              />
            </div>
          </div>

          <div style={ui.divider} />

          {/* 5. TRANSITION */}
          <div style={ui.agendaSection}>
            <div style={ui.agendaTitle}>
              5. TRANSITION (PRIMARY → SECONDARY)
              <RequiredIndicator complete={transitionComplete} />
            </div>
            <div style={ui.agendaPurpose}>
              <strong>Purpose:</strong> Cleanly move from primary into secondary
              without losing energy.
            </div>
            <div style={ui.agendaMeta}>
              Happens: Right after the primary activity ends
            </div>
            <div style={ui.agendaMeta}>Duration: About 1–5 minutes total</div>

            {/* Gap warning: Primary → Transition */}
            {gapPrimaryToTransition > 0 && (
              <div style={{ marginTop: 6, fontSize: 12, color: '#6b7280' }}>
                ℹ️ There is a {gapPrimaryToTransition} {minutesLabel(gapPrimaryToTransition)} gap
                between the Primary end and Transition start. Consider adjusting times.
              </div>
            )}

            {/* Start/End time row (editable) */}
            <div style={{ marginTop: 10 }}>
              <div style={ui.grid2}>
                <div>
                  <div style={ui.fieldLabel}>Start time</div>
                  <TimeSelectDropdown
                    ui={ui}
                    value={transitionStart}
                    reviewLocked={reviewLocked}
                    lockedFieldStyle={lockedFieldStyle}
                    onChange={(v) => {
                      if (reviewLocked) return;
                      setTransitionStartTouched(true);
                      setTransitionStart(v);
                    }}
                  />
                </div>

                <div>
                  <div style={ui.fieldLabel}>End time</div>
                  <TimeSelectDropdown
                    ui={ui}
                    value={transitionEnd}
                    reviewLocked={reviewLocked}
                    lockedFieldStyle={lockedFieldStyle}
                    onChange={(v) => {
                      if (reviewLocked) return;
                      setTransitionEndTouched(true);
                      setTransitionEnd(v);
                    }}
                  />
                </div>
              </div>
            </div>

            <div style={ui.agendaBody}>
              <div style={{ fontWeight: 800, marginTop: 8 }}>What you do:</div>
              Give a quick wrap-up line for the primary.
              <br />
              Clearly say what’s next and where to go.
              <br />
              Physically lead the group so people actually move with you.
            </div>
          </div>

          <div style={ui.divider} />

          {/* 6. SECONDARY ACTIVITY */}
          <div style={ui.agendaSection}>
            <div style={ui.agendaTitle}>
              6. SECONDARY ACTIVITY
              <RequiredIndicator complete={secondaryComplete} />
            </div>
            <div style={ui.agendaPurpose}>
              <strong>Purpose:</strong> Second phase that adds variety and deepens
              connection.
            </div>

            {/* Gap warning: Transition → Secondary */}
            {gapTransitionToSecondary > 0 && (
              <div style={{ marginTop: 6, fontSize: 12, color: '#6b7280' }}>
                ℹ️ There is a {gapTransitionToSecondary} {minutesLabel(gapTransitionToSecondary)} gap
                between the Transition end and Secondary start. Consider adjusting times.
              </div>
            )}

            <div style={{ marginTop: 10 }}>
              <div style={ui.fieldLabel}>Secondary activity name:</div>
              <input
                disabled={reviewLocked}
                style={{
                  ...ui.input,
                  ...(reviewLocked
                    ? { backgroundColor: '#eef0f2', borderColor: '#e5e7eb' }
                    : {}),
                }}
                value={secondaryName}
                onChange={(e) => {
                  if (reviewLocked) return;
                  setSecondaryName(e.target.value);
                }}
                placeholder="Enter secondary activity name"
              />
            </div>

            <div style={ui.grid2}>
              <div>
                <div style={ui.fieldLabel}>Start time:</div>
                <TimeSelectDropdown
                  ui={ui}
                  value={secondaryStart}
                  reviewLocked={reviewLocked}
                  lockedFieldStyle={lockedFieldStyle}
                  onChange={(v) => {
                    if (reviewLocked) return;
                    setSecondaryStartTouched(true);
                    setSecondaryStart(v);
                  }}
                />
              </div>

              <div>
                <div style={ui.fieldLabel}>End time:</div>
                <TimeSelectDropdown
                  ui={ui}
                  value={secondaryEnd}
                  reviewLocked={reviewLocked}
                  lockedFieldStyle={lockedFieldStyle}
                  onChange={(v) => {
                    if (reviewLocked) return;
                    setSecondaryEndTouched(true);
                    secondaryEndAutoRef.current = false;
                    setSecondaryEnd(v);
                  }}
                />
              </div>
            </div>

            <div style={{ marginTop: 6 }}>
              <div style={ui.fieldLabel}>
                Short description of what happens:
              </div>
              <textarea
                disabled={reviewLocked}
                style={{
                  ...ui.textareaLarge,
                  ...(reviewLocked
                    ? { backgroundColor: '#eef0f2', borderColor: '#e5e7eb' }
                    : {}),
                }}
                value={secondaryDescription}
                onChange={(e) => {
                  if (reviewLocked) return;
                  setSecondaryDescription(e.target.value);
                }}
                placeholder="Describe what happens during the secondary activity"
              />
            </div>

            {isExtended && (
              <div style={{ marginTop: 6, fontSize: 12, color: '#b45309' }}>
                ⚠️ This agenda extends the event past the planned end time (
                {formatTime12h(eventEndTime)} → {formatTime12h(agendaEnd)})
              </div>
            )}

            {isShortened && (
              <div style={{ marginTop: 6, fontSize: 12, color: '#6b7280' }}>
                ℹ️ This agenda ends earlier than the planned end time (
                {formatTime12h(eventEndTime)} → {formatTime12h(agendaEnd)})
              </div>
            )}
          </div>

          <div style={ui.divider} />

          {/* 7. CLOSING */}
          <div style={ui.agendaSection}>
            <div style={ui.agendaTitle}>
              7. CLOSING
              <RequiredIndicator complete={closingComplete} />
            </div>
            <div style={ui.agendaPurpose}>
              <strong>Purpose:</strong> Land the night and point people back to Tribe.
            </div>

            {/* Start/End time row (editable) */}
            <div style={{ marginTop: 10 }}>
              <div style={ui.grid2}>
                <div>
                  <div style={ui.fieldLabel}>Start time</div>
                  <TimeSelectDropdown
                    ui={ui}
                    value={closingStart}
                    reviewLocked={reviewLocked}
                    lockedFieldStyle={lockedFieldStyle}
                    onChange={(v) => {
                      if (reviewLocked) return;
                      setClosingStartTouched(true);
                      setClosingStart(v);
                    }}
                  />
                </div>

                <div>
                  <div style={ui.fieldLabel}>End time</div>
                  <TimeSelectDropdown
                    ui={ui}
                    value={closingEnd}
                    reviewLocked={reviewLocked}
                    lockedFieldStyle={lockedFieldStyle}
                    onChange={(v) => {
                      if (reviewLocked) return;
                      setClosingEndTouched(true);
                      setClosingEnd(v);
                    }}
                  />
                </div>
              </div>
            </div>

            <div style={ui.agendaBody}>
              Thank everyone for coming and explain what happens next in Tribe.
            </div>
          </div>

          {/* Safety acknowledgment + submit */}
          <div style={{ marginTop: 18 }}>
            <label style={ui.checkRow}>
              <input
                type="checkbox"
                disabled={reviewLocked}
                checked={acknowledgedSafety}
                onChange={(e) => {
                  if (reviewLocked) return;
                  setAcknowledgedSafety(e.target.checked);
                }}
              />
              <span>
                I understand that I am responsible for maintaining a safe, inclusive
                environment.
              </span>
            </label>
          </div>
        </div>
      </div>
    </>
  );
}
